import { useState } from "react";
import { Sparkles, Lock } from "lucide-react";
import { Button } from "../client/components/ui/button";
import EdgyDrawer from "./EdgyDrawer";
import useTranslation from "../client/hooks/useTranslation";
import { useEdgyAccess } from "./useEdgyAccess";

/**
 * Edgy entry point for a page header's action row.
 *
 * Styled like the topbar's "Add trade" button — same Button variant=default,
 * same h-9/text-sm, same 4x4 icon — so it reads as one of the page's actions
 * rather than a foreign element, and it lives in the same right-aligned action
 * row as the filters.
 *
 * Standard pages each build their own header — there is no shared PageHeader — so
 * this bundles the button with its drawer and is dropped into each one. Custom
 * pages (/p/:id) deliberately do not use it: they are user-composed widget grids.
 */
export default function EdgyHeaderButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const t = useTranslation();
  // Max-only while Edgy is in beta. The button stays enabled on locked plans and
  // opens the drawer's upgrade screen — same rationale as AnalyzeMyTradingButton.
  const { isLocked } = useEdgyAccess();

  return (
    <>
      <Button
        variant="default"
        className={className ?? "h-9 text-sm"}
        onClick={() => setOpen(true)}
        title={isLocked ? t.edgy.betaLock.lockedTooltip : undefined}
      >
        {isLocked ? (
          <Lock className="mr-1.5 h-4 w-4" />
        ) : (
          <Sparkles className="mr-1.5 h-4 w-4" />
        )}
        {t.edgy.buttonLabel}
      </Button>
      <EdgyDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}
